import React from "react";

const TROOP_ICON_BY_ID = {
  warplane: "/battle-sim-icons/warplane.png",
};

const formatStackCount = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return `${Math.round(n)}`;
  if (n < 1000000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
};

const formatStat = (value) => Number(value || 0).toLocaleString();

const normalizeTitanTerminology = (label) => String(label || "")
  .replace(/\bTitan Bison\b/gi, "Source Spirit")
  .replace(/\bBison\b/gi, "Source Spirit")
  .replace(/\bFemale Titan\b/gi, "Empress")
  .replace(/\bMale Titan\b/gi, "Source Spirit");

const formatTierLevel = (troop = {}, statCard = {}) => {
  const familyId = String(troop?.id || "").toLowerCase();
  const level = Number(statCard?.level);
  const levelLabel = Number.isFinite(level) && level >= 0
    ? `L${familyId.includes("titan") || familyId.includes("warplane") ? level + 1 : level}`
    : "";
  return `T${statCard?.tier ?? "-"}${levelLabel ? ` • ${levelLabel}` : ""}`;
};

const getStatusIconLabel = (status = {}) => {
  const key = String(status?.key || status?.label || "").toLowerCase();
  if (key.includes("skill-ban")) return "Blocked";
  if (key.includes("misfire")) return "Misfire";
  if (key.includes("bison-aura")) return "Halo";
  if (key.includes("shield")) return "Shield";
  if (key.includes("guard")) return "Guard";
  if (key.includes("hp-aura")) return "HP";
  if (key.includes("haste")) return "Haste";
  if (key.includes("slow")) return "Slow";
  if (key.includes("vulnerable")) return "Weak";
  return String(status?.label || status?.key || "Status");
};

const BattlefieldStack = ({
  troop,
  count,
  x,
  y,
  side = "attacker",
  statCard,
  showHoverStats = false,
  showDebugOverlay = false,
  stackDebug,
  stackId = "",
  logicalCountLabel = "",
  visualProxyCount = null,
  startupJustRevealed = false,
}) => {
  const sideClass = side === "defender" ? "defender" : "attacker";
  const runtimeState = String(stackDebug?.state || "").toLowerCase();
  const stateClass = runtimeState ? `state-${runtimeState}` : "";
  const startupClass = startupJustRevealed ? "startup-just-revealed" : "";
  const statuses = Array.isArray(stackDebug?.statuses)
    ? stackDebug.statuses.filter((status) => status?.key || status?.label).slice(0, 4)
    : [];
  const statusClass = statuses.length ? "has-status" : "";
  const negativeStatusClass = statuses.some((status) => String(status?.kind || "") === "negative")
    ? "has-negative-status"
    : "";
  const auraStatusClass = statuses.some((status) => String(status?.kind || "") === "aura")
    ? "has-aura-status"
    : "";
  const iconSrc = troop.iconPath || TROOP_ICON_BY_ID[troop.id] || "";
  const shouldShowCount = !troop.hideStackCount;
  const primaryCountLabel = String(logicalCountLabel || "").trim() || formatStackCount(count);
  const secondaryCountLabel = Number.isFinite(Number(visualProxyCount))
    ? String(Math.max(0, Math.floor(Number(visualProxyCount))))
    : "";
  const displayLabel = normalizeTitanTerminology(troop.label);

  return (
    <div
      className={`battle-stack ${sideClass} ${stateClass} ${startupClass} ${statusClass} ${negativeStatusClass} ${auraStatusClass}`.trim()}
      style={{ left: `${x}%`, top: `${y}%` }}
      title={`${displayLabel}${troop.exampleTierName ? ` (${troop.exampleTierName})` : ""}\n${troop.battlefieldRole || ""}\nCount: ${count}`}
    >
      {shouldShowCount ? (
        <span className="battle-stack-count">
          <strong>{primaryCountLabel}</strong>
          {secondaryCountLabel ? <small>V:{secondaryCountLabel}</small> : null}
        </span>
      ) : null}
      {iconSrc ? (
        <img src={iconSrc} alt={`${displayLabel} icon`} className="battle-stack-icon" />
      ) : null}
      <span className="battle-stack-label">{displayLabel}</span>
      {showDebugOverlay && stackDebug?.state ? (
        <span className={`battle-stack-state-badge state-${runtimeState}`}>
          {runtimeState.replace("_", " ")}
        </span>
      ) : null}
      {statuses.length ? (
        <span className="battle-stack-status-list" aria-label={`${displayLabel} statuses`}>
          {statuses.map((status) => {
            const key = String(status?.key || status?.label || "status");
            const cleanKey = key.replace(/[^a-z0-9_-]/gi, "").toLowerCase();
            const kind = String(status?.kind || "neutral").replace(/[^a-z0-9_-]/gi, "").toLowerCase();
            return (
              <span
                key={key}
                className={`battle-stack-status-pill ${kind} ${cleanKey}`.trim()}
                title={String(status?.title || status?.label || key)}
                aria-label={String(status?.title || status?.label || key)}
              >
                <span className="battle-stack-status-text">{getStatusIconLabel(status)}</span>
              </span>
            );
          })}
        </span>
      ) : null}
      {showDebugOverlay && stackDebug?.targetLabel ? (
        <span className="battle-stack-target-label">{stackDebug.targetLabel}</span>
      ) : null}
      {showDebugOverlay && stackId ? (
        <span className="battle-stack-target-label">{stackId}</span>
      ) : null}

      {showHoverStats && statCard ? (
        <div className="battle-stack-hover-card">
          <div className="battle-stack-hover-title">{displayLabel}</div>
          <div>{formatTierLevel(troop, statCard)}</div>
          <div>Count: {formatStat(statCard.count)}</div>
          <div>ATK: {formatStat(statCard?.total?.attack)} ({formatStat(statCard?.perUnit?.attack)})</div>
          <div>HP: {formatStat(statCard?.total?.hp)} ({formatStat(statCard?.perUnit?.hp)})</div>
          <div>DEF: {formatStat(statCard?.total?.defense)} ({formatStat(statCard?.perUnit?.defense)})</div>
          <div>Range: {Number(statCard?.perUnit?.range || 0).toLocaleString()}</div>
          <div>CD: {Number(statCard?.perUnit?.cooldown || 0).toLocaleString()}</div>
        </div>
      ) : null}
    </div>
  );
};

export default BattlefieldStack;
